<template>
  <div class="min-h-screen">
    <Header />
    
    <!-- Hero Section -->
    <section class="page-hero-shell pt-32 pb-20">
      <div class="absolute inset-0 page-overlay-layer bg-pattern opacity-45"></div>
      <div class="hero-orb page-overlay-layer hero-orb-a"></div>
      <div class="hero-orb page-overlay-layer hero-orb-c"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <div class="hero-badge mb-6">
            <span>行业脉冲</span>
          </div>
          <h1 class="text-5xl md:text-7xl font-bold mb-6">
            <span class="gradient-text">新闻资讯</span>
          </h1>
          <p class="text-xl text-gray-700 dark:text-dark-muted max-w-3xl mx-auto">
            了解MAGIC最新动态、行业洞察和技术分享
          </p>
        </div>
      </div>
    </section>

    <!-- Featured News -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-12">精选资讯</h2>
        
        <div class="grid lg:grid-cols-2 gap-8 lg:h-[540px] lg:items-stretch">
          <!-- Main Featured -->
          <div class="h-full lg:min-h-0">
            <div class="card h-full card-hover flex flex-col visual-surface overflow-hidden lg:min-h-0">
              <div class="relative mb-4 overflow-hidden rounded-xl flex-shrink-0">
                <div class="absolute top-3 left-3 z-10">
                  <span class="px-3 py-1 bg-white/90 dark:bg-gray-800/90 text-primary rounded-full text-sm font-medium">
                    热门
                  </span>
                </div>
                <NewsIllustration
                  :type="news[0].illustrationType || 'company'"
                  :variant="news[0].illustrationKey"
                  size="lg"
                  class="!h-[220px] md:!h-[240px] lg:!h-[252px]"
                />
              </div>

              <div class="my-3">
                <span class="text-primary text-sm">{{ news[0].date }}</span>
              </div>

              <h3 class="text-2xl font-bold mb-3 text-dark-text">
                {{ news[0].title }}
              </h3>

              <p class="text-gray-700 dark:text-dark-muted mb-4 line-clamp-3 flex-grow">
                {{ news[0].excerpt }}
              </p>

              <NuxtLink :to="`/news/${news[0].id}`" class="flex items-center text-primary flex-shrink-0 hover:underline">
                查看详情
                <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </NuxtLink>
            </div>
          </div>

          <!-- Secondary Featured -->
          <div class="flex flex-col gap-3 h-full lg:min-h-0">
            <div class="card card-hover visual-surface overflow-hidden lg:flex-1 lg:min-h-0">
              <div class="flex gap-4 items-center py-1 h-full">
                <div class="flex-shrink-0">
                  <NewsIllustration :type="news[1].illustrationType || 'product'" :variant="news[1].illustrationKey" size="sm" />
                </div>

                <div class="flex-1 min-w-0">
                  <div class="mb-1">
                    <span class="text-primary text-sm">{{ news[1].date }}</span>
                  </div>
                  <h3 class="text-base font-bold mb-1 text-dark-text truncate">
                    {{ news[1].title }}
                  </h3>
                  <p class="text-gray-700 dark:text-dark-muted text-sm line-clamp-1">
                    {{ news[1].excerpt }}
                  </p>
                  <NuxtLink :to="`/news/${news[1].id}`" class="text-primary text-xs hover:underline mt-1 inline-block">阅读更多 →</NuxtLink>
                </div>
              </div>
            </div>

            <div class="card card-hover visual-surface overflow-hidden lg:flex-1 lg:min-h-0">
              <div class="flex gap-4 items-center py-1 h-full">
                <div class="flex-shrink-0">
                  <NewsIllustration :type="news[2].illustrationType || 'industry'" :variant="news[2].illustrationKey" size="sm" />
                </div>

                <div class="flex-1 min-w-0">
                  <div class="mb-1">
                    <span class="text-accent text-sm">{{ news[2].date }}</span>
                  </div>
                  <h3 class="text-base font-bold mb-1 text-dark-text truncate">
                    {{ news[2].title }}
                  </h3>
                  <p class="text-gray-700 dark:text-dark-muted text-sm line-clamp-1">
                    {{ news[2].excerpt }}
                  </p>
                  <NuxtLink :to="`/news/${news[2].id}`" class="text-accent text-xs hover:underline mt-1 inline-block">阅读更多 →</NuxtLink>
                </div>
              </div>
            </div>

            <div class="card card-hover visual-surface overflow-hidden lg:flex-1 lg:min-h-0">
              <div class="flex gap-4 items-center py-1 h-full">
                <div class="flex-shrink-0">
                  <NewsIllustration :type="news[3].illustrationType || 'industry'" :variant="news[3].illustrationKey" size="sm" />
                </div>

                <div class="flex-1 min-w-0">
                  <div class="mb-1">
                    <span class="text-primary text-sm">{{ news[3].date }}</span>
                  </div>
                  <h3 class="text-base font-bold mb-1 text-dark-text truncate">
                    {{ news[3].title }}
                  </h3>
                  <p class="text-gray-700 dark:text-dark-muted text-sm line-clamp-1">
                    {{ news[3].excerpt }}
                  </p>
                  <NuxtLink :to="`/news/${news[3].id}`" class="text-primary text-xs hover:underline mt-1 inline-block">阅读更多 →</NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- News Categories -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-wrap gap-4 justify-center mb-12">
          <button 
            v-for="category in categories" 
            :key="category.id"
            @click="selectedCategory = category.id"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-all duration-300',
              selectedCategory === category.id 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-dark-border'
            ]"
          >
            {{ category.name }}
          </button>
        </div>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            v-for="article in filteredNews"
            :key="article.id"
            class="card card-hover flex flex-col visual-surface"
          >
            <div class="relative mb-6 overflow-hidden rounded-xl">
              <NewsIllustration :type="article.illustrationType || 'default'" :variant="article.illustrationKey" size="md" />
            </div>

            <div class="mb-3">
              <span class="text-primary text-sm">{{ article.date }}</span>
              <span class="mx-2 text-gray-700 dark:text-dark-muted">•</span>
              <span class="text-gray-700 dark:text-dark-muted text-sm">{{ article.category }}</span>
            </div>

            <h3 class="text-lg font-bold mb-3 text-dark-text">
              {{ article.title }}
            </h3>

            <p class="text-gray-700 dark:text-dark-muted text-sm mb-4 line-clamp-2 flex-grow">
              {{ article.excerpt }}
            </p>

            <NuxtLink :to="`/news/${article.id}`" class="flex items-center text-primary text-sm mt-auto">
              阅读更多
              <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-32">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl font-bold mb-6 gradient-text">希望获取更多行业洞察？</h2>
        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <button @click="contactUs" class="btn-primary">
            联系我们
          </button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { buildNewsExcerpt, newsArticles } from '~/content/news'

const selectedCategory = ref('all')

const categories = [
  { id: 'all', name: '全部' },
  { id: 'company', name: '公司动态' },
  { id: 'product', name: '产品更新' },
  { id: 'industry', name: '行业洞察' },
  { id: 'tech', name: '技术分享' }
]

const news = computed(() =>
  newsArticles.map((article) => ({
    ...article,
    excerpt: buildNewsExcerpt(article.content)
  }))
)

const filteredNews = computed(() => {
  if (selectedCategory.value === 'all') {
    return news.value
  }
  return news.value.filter(article => article.categoryId === selectedCategory.value)
})

const contactUs = () => {
  window.location.href = '/about#contact'
}
</script>
